import { documentStore } from "@/store/store";
import { TreeNode } from "@/types/tree";
import {
  getDomElementFromTreeNode,
  getElementComputedStyle
} from "@/lib/canvas/helpers";
import { findNode } from "@/lib/tree/tree";

type ElementSide = "top" | "bottom" | "left" | "right";

export function whatSideOfElementIsThePointOver(
  mouseX: number,
  mouseY: number,
  nodeId: number,
  bufferZone = 5
): ElementSide[] | false {
  const node = findNode(documentStore.document.tree, nodeId);

  if (!node) return false;

  const domElement = getDomElementFromTreeNode(node);

  if (!domElement) return false;

  let {
    x: elementX,
    y: elementY,
    width: elementWidth,
    height: elementHeight
  } = domElement.getBoundingClientRect();

  elementX -= bufferZone;
  elementY -= bufferZone;
  elementWidth += bufferZone;
  elementHeight += bufferZone;

  const sides: ElementSide[] = [];

  const insideOnXAxis = () =>
    mouseX >= elementX && mouseX <= elementX + elementWidth;
  const insideOnLeftHalfOfXAxis = () =>
    mouseX >= elementX && mouseX <= elementX + elementWidth / 2;
  const insideOnRightHalfOfXAxis = () =>
    mouseX >= elementX && mouseX > elementX + elementWidth / 2;
  const insideOnYAxis = () =>
    mouseY >= elementY && mouseY <= elementY + elementHeight;
  const insideOnTopHalfOfYAxis = () =>
    mouseY >= elementY && mouseY <= elementY + elementHeight / 2;
  const insideOnBottomHalfOfYAxis = () =>
    mouseY >= elementY && mouseY > elementY + elementHeight / 2;

  if (insideOnXAxis() && insideOnYAxis() && insideOnTopHalfOfYAxis()) {
    sides.push("top");
  }
  if (insideOnXAxis() && insideOnYAxis() && insideOnBottomHalfOfYAxis()) {
    sides.push("bottom");
  }
  if (insideOnXAxis() && insideOnYAxis() && insideOnLeftHalfOfXAxis()) {
    sides.push("left");
  }
  if (insideOnXAxis() && insideOnYAxis() && insideOnRightHalfOfXAxis()) {
    sides.push("right");
  }

  if (sides.length > 0) return sides;
  return false;
}

export function getNodesIdsAtPointInCanvas(x: number, y: number) {
  return window.Breakdance.canvas.document
    .elementsFromPoint(x, y)
    .map(element => {
      // @ts-ignore
      if (element.dataset.nodeId) return element;

      // Find the closest parent node
      return element.closest("[data-node-id]") ?? element;
    })
    .filter(
      // @ts-ignore
      element => element.dataset.nodeId !== undefined
    )
    .map(element => {
      // @ts-ignore
      return parseInt(element.dataset.nodeId);
    });
}

export function getNodeIdAtPointInCanvas(x: number, y: number) {
  return getNodesIdsAtPointInCanvas(x, y).reverse().pop();
}

export function shouldItBeInsertedBeforeOrAfter(
  mouseX: number,
  mouseY: number,
  overNodeId: number,
  parentStackingStyle: "vertical" | "horizontal" = "vertical"
): "before" | "after" | "unknown" {
  const sides = whatSideOfElementIsThePointOver(mouseX, mouseY, overNodeId);
  if (!sides) return "unknown";

  // @ts-ignore
  if (parentStackingStyle === "vertical") {
    if (sides.includes("top")) {
      return "before";
    } else if (sides.includes("bottom")) {
      return "after";
    }
  } // @ts-ignore
  else if (parentStackingStyle === "horizontal") {
    if (sides.includes("left")) {
      return "before";
    } else if (sides.includes("right")) {
      return "after";
    }
  }

  return "unknown";
}

// only works if the element has children.
export function getStackingStyle(node: TreeNode) {
  const defaultStackingStyle = "vertical";

  const childNode = node.children.length > 0 ? node.children[0] : undefined;
  if (!childNode) return defaultStackingStyle;

  const domElement = getDomElementFromTreeNode(childNode);
  if (!domElement) return defaultStackingStyle;

  const computedStyle = getElementComputedStyle(
    domElement.parentNode as HTMLElement
  );

  let stacking: "vertical" | "horizontal" = "vertical";

  const display = computedStyle.getPropertyValue("display");

  if (display === "block") {
    stacking = "vertical";
  } else if (display === "flex") {
    const flexDirection = computedStyle.getPropertyValue("flex-direction");
    if (flexDirection === "row") {
      stacking = "horizontal";
    } else if (flexDirection === "column") {
      stacking = "vertical";
    }
  } else if (display === "grid") {
    stacking = "horizontal";

    /** try to be smarter - stacking is obviously vertical
     * if there's 1 item per row. this only works in the Grid
     * element since it has a bdeGridItemsPerRow variable */
    const itemsPerRow = computedStyle.getPropertyValue(
      "--bde-grid-items-per-row"
    );
    if (itemsPerRow === "1") {
      stacking = "vertical";
    }
  }

  return stacking;

  /* element is horizontally stacked if...
  is flex
  flex direction is column




  what if you are placing next to an inline element in a block element? how could we figure that out?
  we could pass in the actual node that's dragged over, not the parentNode
  and then see if it's inline

  */
}
